import { Edit3, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import EmptyState from "../../components/EmptyState.jsx";
import { Field, Toggle } from "../../components/FormControls.jsx";
import Modal from "../../components/Modal.jsx";
import SearchBar from "../../components/SearchBar.jsx";
import { SkeletonList } from "../../components/Skeleton.jsx";
import { useToast } from "../../context/ToastContext.jsx";
import { deleteRecord, getMasterData, saveRecord } from "../../firebase/firestore.js";

const emptyTeacher = {
  name: "",
  phone: "",
  email: "",
  subjectIds: [],
  subject: "",
  active: true,
  notes: ""
};

export default function TeacherManager() {
  const [master, setMaster] = useState(null);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  async function load(force = false) {
    setLoading(true);
    try {
      setMaster(await getMasterData(force));
    } catch (error) {
      showToast(error.message || "Unable to load teachers", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const teachers = useMemo(() => {
    return (master?.teachers || []).filter((teacher) => {
      const text = `${teacher.name} ${teacher.email} ${teacher.phone} ${teacher.subject} ${teacher.notes}`.toLowerCase();
      return text.includes(search.toLowerCase());
    });
  }, [master, search]);

  async function handleSave(event) {
    event.preventDefault();
    setSaving(true);
    try {
      const subjects = (master?.subjects || []).filter((subject) => modal.form.subjectIds?.includes(subject.id));
      await saveRecord(
        "teachers",
        {
          ...modal.form,
          subject: subjects.map((subject) => subject.name).join(", ")
        },
        modal.id
      );
      showToast("Teacher saved");
      setModal(null);
      await load(true);
    } catch (error) {
      showToast(error.message || "Unable to save teacher", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await deleteRecord("teachers", confirm.id);
      showToast("Teacher deleted");
      setConfirm(null);
      await load(true);
    } catch (error) {
      showToast(error.message || "Unable to delete teacher", "error");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="admin-page">
      <div className="page-title-row">
        <div>
          <p>Master Data</p>
          <h2>Teachers</h2>
        </div>
        <button className="btn btn-primary" type="button" onClick={() => setModal({ id: null, form: emptyTeacher })}>
          <Plus size={18} />
          Add Teacher
        </button>
      </div>
      <SearchBar value={search} onChange={setSearch} placeholder="Search teacher, phone, email, subject" />
      {loading ? (
        <SkeletonList />
      ) : teachers.length ? (
        <div className="manager-grid">
          {teachers.map((teacher) => (
            <article className="card manager-card" key={teacher.id}>
              <div>
                <p className="eyebrow">{teacher.active === false ? "Inactive" : "Active"}</p>
                <h3>{teacher.name}</h3>
                <p>{teacher.subject || "No subject assigned"}</p>
                <p>{teacher.phone || "No phone"} · {teacher.email || "No email"}</p>
              </div>
              <div className="card-actions">
                <button className="icon-button" type="button" onClick={() => setModal({ id: teacher.id, form: { ...emptyTeacher, ...teacher } })}>
                  <Edit3 size={18} />
                </button>
                <button className="icon-button danger" type="button" onClick={() => setConfirm(teacher)}>
                  <Trash2 size={18} />
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <EmptyState title="No teachers found" text="Add the first teacher to start building routines." />
      )}
      <Modal open={Boolean(modal)} title={modal?.id ? "Edit Teacher" : "Add Teacher"} onClose={() => setModal(null)}>
        <form className="form-grid" onSubmit={handleSave}>
          <Field label="Teacher name"><input value={modal?.form.name || ""} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, name: e.target.value } }))} required /></Field>
          <Field label="Phone optional"><input value={modal?.form.phone || ""} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, phone: e.target.value } }))} /></Field>
          <Field label="Email optional"><input type="email" value={modal?.form.email || ""} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, email: e.target.value } }))} /></Field>
          <div className="checkbox-panel">
            <span>Assign subjects</span>
            {(master?.subjects || []).map((subject) => (
              <label key={subject.id}>
                <input
                  type="checkbox"
                  checked={modal?.form.subjectIds?.includes(subject.id) || false}
                  onChange={(event) => {
                    const current = modal.form.subjectIds || [];
                    const next = event.target.checked ? [...current, subject.id] : current.filter((id) => id !== subject.id);
                    setModal((m) => ({ ...m, form: { ...m.form, subjectIds: next } }));
                  }}
                />
                {subject.icon || "📘"} {subject.name}
              </label>
            ))}
          </div>
          <Field label="Notes"><textarea value={modal?.form.notes || ""} onChange={(e) => setModal((m) => ({ ...m, form: { ...m.form, notes: e.target.value } }))} /></Field>
          <Toggle label="Active teacher" checked={modal?.form.active !== false} onChange={(value) => setModal((m) => ({ ...m, form: { ...m.form, active: value } }))} />
          <div className="form-actions">
            <button className="btn btn-ghost" type="button" onClick={() => setModal(null)}>Cancel</button>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? <span className="spinner" /> : null}Save</button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={Boolean(confirm)}
        title="Delete teacher?"
        message={`Delete ${confirm?.name || "this teacher"}?`}
        loading={saving}
        onCancel={() => setConfirm(null)}
        onConfirm={handleDelete}
      />
    </section>
  );
}
